import PlusCircleOutlined from '@ant-design/icons/PlusCircleOutlined'
import { Input, Spin } from 'antd'
import CantileverButton from 'components/CantileverButton'
import dayjs from 'dayjs'
import { EditSaveCancelButtons } from 'features/assetDatabase/components/assetOverview/EditSaveCancelButtons'
import { useRolodexEdit } from 'features/fde'
import { useGeckoPermissions } from 'features/permissions'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import { GeckoUnit, RolodexEntryPatchJson } from 'types'

const NotesContainter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: left;
  width: 50%;
  padding: 1 0.5rem;
`

interface NotesSectionProps {
  unit: GeckoUnit | undefined
  refreshData: () => Promise<void>
  style?: React.CSSProperties
}

export const NotesSection: React.FC<NotesSectionProps> = ({
  unit,
  refreshData,
  style,
}) => {
  const notes = unit?.data.unitBrief
  const [isEditingNotes, setIsEditingNotes] = useState(false)
  const [newNoteText, setNewNoteText] = useState<string | undefined>(
    notes?.text
  )
  const { user: currentUser } = useGeckoPermissions()
  const { rolodexPatch } = useRolodexEdit()
  const [isLoading, setIsLoading] = useState(false)
  const { theme } = useGeckoTheme()

  const handleEditNotes = async (notes: string | undefined) => {
    if (!notes || notes.length === 0 || !unit) {
      return
    }

    setIsLoading(true)
    const currentTimestamp = dayjs().unix()

    const patchData: RolodexEntryPatchJson = {
      id: unit?.id,
      data: {
        unitBrief: {
          user_id: currentUser?.id,
          user_name: currentUser?.name,
          text: notes,
          timestamp: currentTimestamp,
        },
      },
    }

    await rolodexPatch([patchData]).then(() => refreshData())
    setIsLoading(false)
    setIsEditingNotes(false)
  }

  useEffect(() => {
    setNewNoteText(notes?.text)
  }, [notes])

  const handleEdit = () => {
    setIsEditingNotes(true)
  }
  const handleCancel = () => {
    setIsEditingNotes(false)
  }
  const handleSave = () => {
    handleEditNotes(newNoteText)
  }

  return (
    <NotesContainter style={style}>
      <Spin spinning={isLoading}>
        <div style={{ paddingTop: '1rem' }}>
          {isEditingNotes ? (
            <Input.TextArea
              style={{ backgroundColor: theme.forecast.background3 }}
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              rows={6}
            />
          ) : (
            notes && (
              <>
                <p>{notes.text}</p>
              </>
            )
          )}
        </div>
      </Spin>
      {notes || (!notes && isEditingNotes) ? (
        <div style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
          <EditSaveCancelButtons
            saveCancelButtons={isEditingNotes}
            handleEdit={handleEdit}
            handleCancel={handleCancel}
            handleSave={handleSave}
            formID='notes'
          />
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}>
          <CantileverButton
            ghost
            onClick={() => setIsEditingNotes(true)}
            style={{ width: '40%' }}>
            <PlusCircleOutlined />
            Add Note
          </CantileverButton>
        </div>
      )}
      {notes && !isEditingNotes && (
        <div
          style={{
            paddingTop: '1rem',
            paddingBottom: '0.5rem',
          }}>
          <span
            style={{
              color: theme.forecast.secondaryText,
            }}>
            Updated {dayjs.unix(notes.timestamp).format('MMM D, YYYY')} by{' '}
            {notes.user_name}
          </span>
        </div>
      )}
    </NotesContainter>
  )
}
